import React, { useState, useEffect, useLayoutEffect, ReactNode, useCallback, useMemo, useRef, cloneElement, isValidElement, Children } from 'react';
import { BaseRecord, CrudFilters, useList } from '@refinedev/core';
import debounce from 'lodash.debounce';
import {
  AutoComplete as AntdAutoComplete,
  AutoCompleteProps as AntdAutoCompleteProps,
  SelectProps as AntdSelectProps,
  Button,
  Space,
  Typography,
} from 'antd';

import { dropdownStyles, dropdownSpaceStyles, dropdownSpaceButtonStyles } from './styles';

export interface Option<T> extends BaseRecord {
  value: string;
  label: ReactNode;
  data: T;
}

export interface AutoCompleteProps<T> extends AntdSelectProps<T, Option<T>>, AntdAutoCompleteProps<T, Option<T>> {
  dropdownButtonLabel?: string;
  onClickDropdownButton?: VoidFunction;
  resource?: string;
  mapItem: (data: T, index: number) => Option<T>;
  refetchOnEmptyValue?: boolean;
  additionalFilters?: CrudFilters;
  disabled?: boolean;
  onClear?: () => void;
  notFoundPlaceholder?: string;
  pageSize?: number;
  enableLoadMore?: boolean;
  enableNotHasData?: boolean;
  prioritizeFiatType?: boolean;
  readOnly?: boolean;
  disableInput?: boolean;
  clearOnBlurIfUnselected?: boolean;
  setLoading?: (loading: boolean) => void;
  setData?: (data: any[]) => void;
}
// TODO: rework cleaner, separate data mapping from label render, debounce, trim
export const AutoComplete = <T extends BaseRecord>({
  dropdownButtonLabel,
  onClickDropdownButton,
  resource,
  mapItem,
  children,
  refetchOnEmptyValue,
  additionalFilters = [],
  disabled = false,
  onClear,
  notFoundPlaceholder = 'Not found',
  pageSize = 20,
  enableLoadMore = false,
  enableNotHasData = false,
  prioritizeFiatType = false,
  readOnly = false,
  disableInput = false,
  clearOnBlurIfUnselected = false,
  setLoading,
  setData,
  ...props
}: AutoCompleteProps<T>) => {
  const [value, setValue] = useState<string>();
  const [options, setOptions] = useState<Option<T>[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSelected, setIsSelected] = useState(false);
  const allLoadedOptionsRef = useRef<Option<T>[]>([]);
  const currentSearchValueRef = useRef<string>('');
  const currentPageRef = useRef<number>(1);

  const [notHasData, setNotHasData] = useState(false);

  // Track current input value to check if input has data
  const currentInputValue = props.value || value;
  const hasInputValue = !!currentInputValue && String(currentInputValue).trim().length > 0;

  const debouncedSetValue = useMemo(() => debounce(setValue, 500, { trailing: true }), []);

  // Only add contains filter if not selected (to avoid _like search when item is selected)
  const searchFilters = isSelected ? [] : [{ field: '', operator: 'contains' as const, value }];
  const { refetch, isLoading } = useList<T>({
    resource,
    filters: [...searchFilters, ...additionalFilters],
    pagination: {
      current: currentPage,
      pageSize,
    },
    queryOptions: {
      enabled: !disabled,
      onSuccess: ({ data }): any => {
        // Handle different response structures
        // For asset-config-groups/get-all: data?.data?.data
        // For other APIs: data or data?.data
        let filteredData: any[] = [];
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          const dataObj = data as any;
          if (dataObj?.data?.data && Array.isArray(dataObj.data.data)) {
            filteredData = dataObj.data.data;
          } else if (dataObj?.data && Array.isArray(dataObj.data)) {
            filteredData = dataObj.data;
          } else if (dataObj?.data?.items && Array.isArray(dataObj?.data?.items)) {
            filteredData = dataObj?.data?.items;
          }
        } else if (Array.isArray(data)) {
          filteredData = data;
        }

        let newOptions = filteredData.map(mapItem);

        // Sort: prioritize items with type 'FIAT' to the top (only when prioritizeFiatType is true)
        if (prioritizeFiatType) {
          newOptions = newOptions.sort((a, b) => {
            const aType = (a.data as any)?.type;
            const bType = (b.data as any)?.type;
            // FIAT items come first
            if (aType === 'FIAT' && bType !== 'FIAT') return -1;
            if (aType !== 'FIAT' && bType === 'FIAT') return 1;
            // Keep original order for items with same type
            return 0;
          });
        }

        const pageAtFetchTime = currentPageRef.current;

        // Set notHasData based on whether we have data
        if (pageAtFetchTime === 1) {
          // Only check on first page load/search
          setNotHasData(newOptions.length === 0);
        }

        if (enableLoadMore && pageAtFetchTime === 1) {
          // First load or search changed - replace options
          allLoadedOptionsRef.current = newOptions;
          setOptions(newOptions);
          setData?.(newOptions ?? []);
          setHasMore(newOptions.length >= pageSize);
        } else if (enableLoadMore && pageAtFetchTime > 1) {
          // Load more - append to existing options (concat vào list cũ)
          const uniqueNewOptions = newOptions.filter(
            newOpt => !allLoadedOptionsRef.current.some(existingOpt => existingOpt?.data?.id === newOpt?.data?.id)
          );

          // Sort unique new options if prioritizeFiatType is enabled
          const sortedUniqueNewOptions = prioritizeFiatType
            ? uniqueNewOptions.sort((a, b) => {
              const aType = (a.data as any)?.type;
              const bType = (b.data as any)?.type;
              if (aType === 'FIAT' && bType !== 'FIAT') return -1;
              if (aType !== 'FIAT' && bType === 'FIAT') return 1;
              return 0;
            })
            : uniqueNewOptions;
          // Concat new options vào list cũ
          allLoadedOptionsRef.current = [...allLoadedOptionsRef.current, ...sortedUniqueNewOptions];
          // Re-sort the entire list to maintain FIAT priority
          if (prioritizeFiatType) {
            allLoadedOptionsRef.current = allLoadedOptionsRef.current.sort((a, b) => {
              const aType = (a.data as any)?.type;
              const bType = (b.data as any)?.type;
              if (aType === 'FIAT' && bType !== 'FIAT') return -1;
              if (aType !== 'FIAT' && bType === 'FIAT') return 1;
              return 0;
            });
          }
          setOptions(allLoadedOptionsRef.current);
          setData?.(allLoadedOptionsRef.current.map(item => item.data) ?? []);
          setHasMore(uniqueNewOptions.length >= pageSize);
          setIsLoadingMore(false);
        } else {
          setIsLoadingMore(false);
          // Normal mode - replace options
          setOptions(newOptions);
          setData?.(newOptions.map(item => item.data) ?? []);
        }
      },
    },
  });

  useEffect(() => {
    setLoading?.(isLoading);
  }, [isLoading, setLoading]);

  // Reset pagination when search value changes
  useEffect(() => {
    if (currentSearchValueRef.current !== value) {
      setCurrentPage(1);
      currentPageRef.current = 1;
      allLoadedOptionsRef.current = [];
      setHasMore(true);
      // Reset notHasData when search value changes (will be updated by onSuccess)
      setNotHasData(false);
      currentSearchValueRef.current = value || '';

      // Trigger refetch when search value changes (reset to page 1)
      if (!disabled && (refetchOnEmptyValue || value)) {
        refetch();
      }
    }
  }, [value, disabled, refetchOnEmptyValue, refetch]);

  // Refetch when value or other dependencies change (but not when currentPage changes for load more)
  useEffect(() => {
    if (!refetchOnEmptyValue && !value) {
      setOptions([]);
      allLoadedOptionsRef.current = [];
      setCurrentPage(1);
      currentPageRef.current = 1;
      setHasMore(true);
      // Don't set notHasData here - let onSuccess handle it based on API response
    }
  }, [refetchOnEmptyValue, value]);

  // Refetch when currentPage changes for load more (only when page > 1)
  useEffect(() => {
    if (enableLoadMore && currentPage > 1 && !disabled) {
      // Update ref to track current page at fetch time
      currentPageRef.current = currentPage;
      // Trigger refetch when page changes for load more
      // This will load new data and onSuccess will append to existing options
      refetch();
    }
  }, [currentPage, enableLoadMore, disabled, refetch]);

  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore && !disabled) {
      setIsLoadingMore(true);
      // Increase page - this will trigger useEffect above to refetch
      setCurrentPage(prev => prev + 1);
    }
  }, [isLoadingMore, hasMore, disabled]);

  const onInputKeyDown = useCallback((e: any) => {
    if (readOnly || disableInput) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  }, [readOnly, disableInput]);

  const onSearch = useCallback(
    (newValue: string) => {
      if (readOnly || disableInput) {
        return;
      }
      // Reset selected flag when user searches again - enable _like search
      if (newValue && newValue !== value) {
        setIsSelected(false);
      }
      debouncedSetValue(newValue);
    },
    [debouncedSetValue, value, readOnly, disableInput],
  );

  // Handler để chặn các sự kiện input khi readOnly hoặc disableInput
  const handleInputEvents = useCallback((e: React.KeyboardEvent | React.MouseEvent | React.FormEvent) => {
    if (readOnly || disableInput) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, [readOnly, disableInput]);

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (clearOnBlurIfUnselected && !isSelected) {
        setIsSelected(false);
        setNotHasData(false);
        debouncedSetValue(undefined);
        onClear?.();
      }
      props.onBlur?.(e);
    },
    [clearOnBlurIfUnselected, debouncedSetValue, isSelected, onClear, props],
  );

  // Track dropdown visibility so when `options` updates we can keep it open (no remount-by-key; optional force reopen).
  const dropdownOpenRef = useRef(false);
  const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);

  const { open: openFromProps, onOpenChange: onOpenChangeFromProps, ...propsRest } = props;

  const mergedOpen = openFromProps !== undefined ? openFromProps : internalDropdownOpen;

  const handleOpenChange = useCallback(
    (visible: boolean) => {
      dropdownOpenRef.current = visible;
      if (openFromProps === undefined) {
        setInternalDropdownOpen(visible);
      }
      onOpenChangeFromProps?.(visible);
    },
    [openFromProps, onOpenChangeFromProps],
  );

  useLayoutEffect(() => {
    if (!dropdownOpenRef.current) {
      return;
    }
    if (openFromProps !== undefined) {
      return;
    }
    // Options replaced while panel was open — rc-select may close; keep open.
    setInternalDropdownOpen(true);
  }, [options, openFromProps]);

  return (
    <AntdAutoComplete
      {...propsRest}
      open={mergedOpen}
      onOpenChange={handleOpenChange}
      filterOption={false}
      options={options}
      onSearch={onSearch}
      dropdownStyle={dropdownStyles}
      disabled={disabled || (notHasData && !(enableNotHasData && hasInputValue))}
      notFoundContent={<Typography.Text style={{ padding: '10px' }}>{notFoundPlaceholder}</Typography.Text>}
      onInputKeyDown={onInputKeyDown}
      onBlur={handleBlur}
      onClear={() => {
        // Reset selected flag when cleared - enable _like search again
        setIsSelected(false);
        setNotHasData(false);
        debouncedSetValue(undefined);
        onClear?.();
      }}
      onSelect={(value, data) => {
        // Set selected flag to true - this will disable _like search
        setIsSelected(true);
        debouncedSetValue(data.value);
        props.onSelect?.(value, data);
      }}
      dropdownRender={menu => (
        <>
          {menu}
          {enableLoadMore && hasMore && !isLoading && !isLoadingMore && (
            <Space direction='horizontal' style={dropdownSpaceStyles}>
              <Button
                type='text'
                style={dropdownSpaceButtonStyles}
                onClick={handleLoadMore}
                loading={isLoadingMore}
              >
                {'View more'}
              </Button>
            </Space>
          )}
          {!enableLoadMore && dropdownButtonLabel && (
            <Space direction='horizontal' style={dropdownSpaceStyles}>
              <Button type='text' style={dropdownSpaceButtonStyles} onClick={onClickDropdownButton}>
                {dropdownButtonLabel}
              </Button>
            </Space>
          )}
        </>
      )}
    >
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          const childDisabled = (child as React.ReactElement<any>).props?.disabled;
          // If enableNotHasData is true and input has value, don't disable even if notHasData is true
          // Priority: child's disabled > (notHasData && !(enableNotHasData && hasInputValue)) > component's disabled prop
          const shouldDisableByNotHasData = notHasData === true && enableNotHasData && !hasInputValue;
          const finalDisabled = childDisabled || shouldDisableByNotHasData || disabled;
          const childReadOnly = (child as React.ReactElement<any>).props?.readOnly;
          // Nếu readOnly hoặc disableInput được bật, đảm bảo input cũng readOnly
          const finalReadOnly = childReadOnly !== undefined ? childReadOnly : (readOnly || disableInput);
          return cloneElement(child as React.ReactElement<any>, {
            disabled: finalDisabled,
            readOnly: finalReadOnly,
            onKeyDown: (e: React.KeyboardEvent) => {
              handleInputEvents(e);
              (child as React.ReactElement<any>).props?.onKeyDown?.(e);
            },
            onKeyPress: (e: React.KeyboardEvent) => {
              handleInputEvents(e);
              (child as React.ReactElement<any>).props?.onKeyPress?.(e);
            },
            onKeyUp: (e: React.KeyboardEvent) => {
              handleInputEvents(e);
              (child as React.ReactElement<any>).props?.onKeyUp?.(e);
            },
            onClick: (e: React.MouseEvent) => {
              if (readOnly || disableInput) {
                e.preventDefault();
                e.stopPropagation();
              }
              (child as React.ReactElement<any>).props?.onClick?.(e);
            },
            onInput: (e: React.FormEvent) => {
              if (readOnly || disableInput) {
                e.preventDefault();
                e.stopPropagation();
              }
              (child as React.ReactElement<any>).props?.onInput?.(e);
            },
            onBeforeInput: (e: React.FormEvent) => {
              if (readOnly || disableInput) {
                e.preventDefault();
                e.stopPropagation();
              }
              (child as React.ReactElement<any>).props?.onBeforeInput?.(e);
            },
            onFocus: (e: React.FocusEvent) => {
              if (readOnly || disableInput) {
                // Blur ngay lập tức để chặn cursor nháy
                (e.target as HTMLElement).blur();
              }
              (child as React.ReactElement<any>).props?.onFocus?.(e);
            },
            style: {
              ...(child as React.ReactElement<any>).props?.style,
              ...(readOnly || disableInput ? {
                cursor: 'default',
                caretColor: 'transparent',
              } : {}),
            },
          });
        }
        return child;
      })}
    </AntdAutoComplete>
  );
};
